import { useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@/assets/icons/search-icon.svg';
import HistoryTabs from './HistoryTabs';
import HistoryItem from './HistoryItem';
import TagSubmissionItem from './TagSubmissionItem';
import { useHistory, HistoryTab } from '@/hooks/useHistory';
import { UserActivity } from '@/types/user.types';
import { TagSubmissionHistoryItem } from '@/services/tagSubmissionHistory.service';
import { GroupedSpecificationItem } from '@/types/groupedHistory.types';
import './History.scss';

export interface HistoryRef {
  setActiveTab: (tab: HistoryTab) => void;
}

const History = forwardRef<HistoryRef, {}>((_, ref) => {
  const { t } = useTranslation('home');
  const [activeTab, setActiveTab] = useState<HistoryTab>('all');

  useImperativeHandle(ref, () => ({
    setActiveTab: (tab: HistoryTab) => {
      setActiveTab(tab);
    }
  }));
  const [searchQuery, setSearchQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const { historyItems, tabCounts, isLoading } = useHistory(activeTab, searchQuery);

  const displayedItems = showAll ? historyItems : historyItems.slice(0, 4);

  if (isLoading) {
    return (
      <div className="history history--loading">
        <div className="history__loading-message">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="history">
      <div className="history__header">
        <HistoryTabs 
          activeTab={activeTab} 
          onTabChange={setActiveTab}
          tabCounts={tabCounts}
        />
        <div className="history__search">
          <img src={SearchIcon} alt="search" className="history__search-icon" />
          <input
            type="text"
            placeholder={t('history.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      
        <div className="history__timeline">
          {displayedItems.map((item, index) => {
            // Check if the item is a TagSubmissionHistoryItem by checking for lastModifiedDate property
            const isTagSubmission = 'lastModifiedDate' in item;
            // Check if the item is a GroupedSpecificationItem by checking for items property
            const isGroupedItem = 'items' in item;
            
            if (isTagSubmission) {
              return (
                <TagSubmissionItem
                  key={index}
                  item={item as TagSubmissionHistoryItem}
                  isLastVisible={!showAll && index === 3 && historyItems.length > 4}
                />
              );
            } else {
              return (
                <HistoryItem
                  key={index}
                  item={isGroupedItem ? (item as GroupedSpecificationItem) : (item as UserActivity)}
                  isLastVisible={!showAll && index === 3 && historyItems.length > 4}
                />
              );
            }
          })}
        </div>

      {historyItems.length > 4 && (
        <button 
          className="history__see-more"
          onClick={() => setShowAll(!showAll)}
        >
          {t(showAll ? 'history.seeLess' : 'history.seeMore')}
        </button>
      )}
    </div>
  );
});

export default History;
